import React, { useState, useEffect, useRef } from "react";
import { Copy, Upload, Download, Play, Square, Loader, History, Check, AlertCircle, Sparkles } from "lucide-react";

const BACKEND_URL = "https://1mwkkhdk52.execute-api.ca-central-1.amazonaws.com";

interface ProcessingLog {
  id: string;
  timestamp: number;
  original: string;
  action: "keep" | "replace" | "delete";
  modified?: string;
  reason: string;
  error?: boolean;
}

const SLOP_WORDS = [
  "delve", "delved", "tapestry", "intricate", "vibrant", "testament", "intertwined",
  "realm", "beacon of hope", "navigate", "complexities", "symphony",
  "human spirit", "poignant", "unwavering", "resilience", "bustling",
  "rich history", "nuance", "woven", "fabric", "threads", "unfoldment",
  "orchestrate", "meticulous", "seamless"
];

const SLOP_PATTERNS = [
  /not (?:just|merely|only).*but also/i,
  /not because.*but because/i,
  /^in conclusion/i,
  /^ultimately,/i,
  /^moreover,/i,
  /it is worth noting that/i,
  /in the landscape of/i,
  /as a [a-z\s]+, I/i
];

function localReplaceSlop(sentence: string): string {
  let modified = sentence;
  const wordReplacements: Record<string, string> = {
    "delve into": "explore",
    "delved into": "explored",
    "delve": "explore",
    "delved": "explored",
    "tapestry": "collection",
    "intricate": "detailed",
    "vibrant": "lively",
    "testament to": "example of",
    "testament": "example",
    "intertwined": "connected",
    "realm": "area",
    "beacon of hope": "source of inspiration",
    "navigate": "handle",
    "complexities": "details",
    "symphony": "blend",
    "human spirit": "human nature",
    "poignant": "touching",
    "unwavering": "steady",
    "resilience": "endurance",
    "bustling": "busy",
    "rich history": "history",
    "nuance": "subtlety",
    "woven": "built",
    "fabric": "structure",
    "threads": "parts",
    "unfoldment": "development",
    "orchestrate": "arrange",
    "meticulous": "careful",
    "seamless": "smooth"
  };

  for (const [slop, rep] of Object.entries(wordReplacements)) {
    const regex = new RegExp(`\\b${slop}\\b`, 'gi');
    modified = modified.replace(regex, rep);
  }

  return modified;
}

function localEvaluateSlop(sentence: string): { action: "keep"| "replace" | "delete", modifiedSentence: string, reason: string } {
  for (const pattern of SLOP_PATTERNS) {
    if (pattern.test(sentence)) {
      return { action: "delete", modifiedSentence: "", reason: "Local fallback: Structural slop." };
    }
  }

  const modified = localReplaceSlop(sentence);
  if (modified !== sentence) {
    return { action: "replace", modifiedSentence: modified, reason: "Local fallback: Replaced slop words." };
  }
  return { action: "keep", modifiedSentence: sentence, reason: "Local fallback: No slop found." };
}

function containsSlop(sentence: string): boolean {
  const lower = sentence.toLowerCase();
  
  for (const word of SLOP_WORDS) {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    if (regex.test(lower)) return true;
  }
  
  for (const pattern of SLOP_PATTERNS) {
    if (pattern.test(lower)) return true;
  }
  
  return false;
}

export const AntiSlop: React.FC = () => {
  const [inputText, setInputText] = useState("");
  const [resultText, setResultText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currentSentence, setCurrentSentence] = useState("");
  const [logs, setLogs] = useState<ProcessingLog[]>([]);
  const [useHeuristicsOnly, setUseHeuristicsOnly] = useState(true);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const cancelRef = useRef(false);
  const fallbackOnlyRef = useRef(false);
  const logsEndRef = useRef<HTMLDivElement>(null);

  const SAMPLE_AI_ESSAY = `In today's fast-paced digital landscape, it is a testament to the vibrant tapestry of the human spirit to delve into the intricate complexities of modern technology. Ultimately, as an AI-driven society, we must orchestrate a seamless harmony between tradition and innovation. Moreover, it is worth noting that our unwavering resilience remains a beacon of hope for future generations. Not only does this paradigm foster multifaceted growth, but it also underscores the pivotal essence of sustainable progress. In conclusion, it is important to remember that the profound nuances of this journey are fundamentally transformative.`;

  const SAMPLE_CORPORATE = `Furthermore, by spearheading a synergistic paradigm shift, we can seamlessly leverage cutting-edge capabilities to drive holistic transformation. It is crucial to acknowledge that our robust ecosystem acts as a testament to our steadfast commitment. In summary, delving into these mission-critical imperatives will undeniably unlock unprecedented value.`;

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Auto-scroll logs
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setInputText(text);
      showToast(`Loaded ${file.name} successfully!`);
    } catch (err) {
      console.error("Error parsing file", err);
      showToast("Failed to read the selected file.");
    }
    e.target.value = "";
  };

  const startProcessing = async () => {
    if (!inputText.trim()) return;
    
    setIsProcessing(true);
    setResultText("");
    setLogs([]);
    setIsRateLimited(false);
    cancelRef.current = false;
    fallbackOnlyRef.current = useHeuristicsOnly;

    const parts = inputText.split(/((?:\r?\n){2,})/);
    const sentenceRegex = /([^.!?-]+(?:[.!?]+(?:['"])?)*)(\s*)/g;
    
    let totalSentences = 0;
    for (let i = 0; i < parts.length; i += 2) {
      const p = parts[i];
      if (p.trim()) {
        totalSentences += Array.from(p.matchAll(sentenceRegex)).length;
      }
    }
    setProgress({ current: 0, total: totalSentences });

    let currentProcessed = 0;
    const finalParts: string[] = [];

    for (let pIdx = 0; pIdx < parts.length; pIdx++) {
      if (cancelRef.current) break;

      if (pIdx % 2 !== 0) {
        finalParts.push(parts[pIdx]);
        continue;
      }

      const p = parts[pIdx];
      if (!p.trim()) {
        finalParts.push(p);
        continue;
      }

      const matches = Array.from(p.matchAll(sentenceRegex));
      let cleanedParagraph = "";

      for (let sIdx = 0; sIdx < matches.length; sIdx++) {
        let success = false;
        if (cancelRef.current) break;

        const match = matches[sIdx];
        const fullSentence = match[0];
        const sentenceContent = match[1];
        const trailingWhitespace = match[2];

        const textToEval = sentenceContent.trim();
        if (!textToEval) {
          cleanedParagraph += fullSentence;
          currentProcessed++;
          setProgress({ current: currentProcessed, total: totalSentences });
          continue;
        }

        const leadingWhitespace = sentenceContent.slice(0, sentenceContent.indexOf(textToEval));
        setCurrentSentence(textToEval);

        if (!containsSlop(textToEval)) {
            cleanedParagraph += leadingWhitespace + textToEval + trailingWhitespace;
            currentProcessed++;
            setProgress({ current: currentProcessed, total: totalSentences });
            await new Promise(r => setTimeout(r, 20));
            continue;
        }

        if (fallbackOnlyRef.current) {
            const localResult = localEvaluateSlop(textToEval);
            
            if (localResult.action !== "keep") {
              setLogs(prev => [...prev, {
                id: Math.random().toString(),
                timestamp: Date.now(),
                original: textToEval,
                action: localResult.action,
                modified: localResult.modifiedSentence,
                reason: localResult.reason
              }]);
            }

            if (localResult.action === "replace") {
              cleanedParagraph += leadingWhitespace + localResult.modifiedSentence + trailingWhitespace;
            } else if (localResult.action === "keep") {
              cleanedParagraph += fullSentence;
            }
            
            currentProcessed++;
            setProgress({ current: currentProcessed, total: totalSentences });
            await new Promise(r => setTimeout(r, 20));
            continue;
        }

        let retryCount = 0;

        while (!success && retryCount < 5) {
          if (cancelRef.current) break;
          const startMs = Date.now();
          
          try {
            let res: Response;
            try {
              res = await fetch(`${BACKEND_URL}/evaluate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ sentence: textToEval })
              });
            } catch (fetchErr: any) {
              throw new Error(`Network error: ${fetchErr.message}`);
            }

            if (res.status === 429 || res.status === 503) {
              const errorData = await res.json().catch(() => ({}));
              const isDailyQuota = (errorData.error || "").toLowerCase().includes("perday");

              if (retryCount >= 1 || isDailyQuota) {
                console.warn("Falling back to local heuristic permanently due to quota or persistent errors.");
                fallbackOnlyRef.current = true;
                setUseHeuristicsOnly(true);
                setIsRateLimited(true);

                const localResult = localEvaluateSlop(textToEval);
                if (localResult.action !== "keep") {
                  setLogs(prev => [...prev, {
                    id: Math.random().toString(),
                    timestamp: Date.now(),
                    original: textToEval,
                    action: localResult.action,
                    modified: localResult.modifiedSentence,
                    reason: localResult.reason
                  }]);
                }

                if (localResult.action === "replace") {
                  cleanedParagraph += leadingWhitespace + localResult.modifiedSentence + trailingWhitespace;
                } else if (localResult.action === "keep") {
                  cleanedParagraph += fullSentence;
                }
                success = true;
                continue;
              }

              let delayMs = 15000;
              const matchError = (errorData.error || "").match(/retry in ([\d.]+)s/i);
              if (matchError && !isNaN(parseFloat(matchError[1]))) {
                delayMs = Math.ceil(parseFloat(matchError[1])) * 1000 + 2000;
              }
              console.warn(`Rate limited (${res.status}). Retrying in ${delayMs / 1000} seconds...`);
              await new Promise(r => setTimeout(r, delayMs));
              retryCount++;
              continue;
            }

            if (!res.ok) throw new Error(`Server error: ${res.status}`);

            const result = await res.json();
            
            if (result.action !== "keep") {
              setLogs(prev => [...prev, {
                id: Math.random().toString(),
                timestamp: Date.now(),
                original: textToEval,
                action: result.action,
                modified: result.action === "replace" ? result.modifiedSentence : "",
                reason: result.reason
              }]);
            }

            if (result.action === "replace") {
              cleanedParagraph += leadingWhitespace + result.modifiedSentence + trailingWhitespace;
            } else if (result.action === "keep") {
              cleanedParagraph += fullSentence;
            }
            success = true;
          } catch (err: any) {
            console.error(err);
            retryCount++;
            if (retryCount >= 2) {
                console.warn("Falling back to local heuristic due to network error.");
                const localResult = localEvaluateSlop(textToEval);
                if (localResult.action !== "keep") {
                  setLogs(prev => [...prev, {
                    id: Math.random().toString(),
                    timestamp: Date.now(),
                    original: textToEval,
                    action: localResult.action,
                    modified: localResult.modifiedSentence,
                    reason: localResult.reason
                  }]);
                }

                if (localResult.action === "replace") {
                  cleanedParagraph += leadingWhitespace + localResult.modifiedSentence + trailingWhitespace;
                } else if (localResult.action === "keep") {
                  cleanedParagraph += fullSentence;
                }
                success = true;
            } else {
              await new Promise(r => setTimeout(r, 5000));
            }
          }

          if (success && !cancelRef.current && currentProcessed + 1 < totalSentences && !fallbackOnlyRef.current) {
            const elapsed = Date.now() - startMs;
            const delay = Math.max(0, 4200 - elapsed);
            await new Promise((r) => setTimeout(r, delay));
          }
        }

        currentProcessed++;
        setProgress({ current: currentProcessed, total: totalSentences });
      }

      finalParts.push(cleanedParagraph);
    }

    setResultText(finalParts.join(""));
    setIsProcessing(false);
    setCurrentSentence("");
  };

  const handleStop = () => {
    cancelRef.current = true;
    setIsProcessing(false);
    setCurrentSentence("");
  };

  const downloadText = (extension: "txt" | "md") => {
    const blob = new Blob([resultText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cleaned_${Date.now()}.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadLogs = () => {
    const blob = new Blob([JSON.stringify(logs, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `interventions_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(resultText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getStats = (text: string) => {
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const minutes = Math.max(1, Math.ceil(words / 200));
    return { words, time: words > 0 ? `${minutes} min read` : '0 min read' };
  };

  const inputStats = getStats(inputText);
  const resultStats = getStats(resultText);

  return (
    <div className="bg-slate-900/40 rounded-3xl border border-slate-800 p-8 backdrop-blur-xl">
      <header className="flex flex-col md:flex-row items-center justify-between gap-6 pb-8 border-b border-slate-800">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight text-white flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-purple-400" /> Veritas Anti-Slop Compiler
          </h2>
          <p className="text-slate-400 mt-1 text-sm font-light">Eradicate AI clichés, hyper-parallelism, and generic structural crutches instantly. (Unwired to avoid token waste; local heuristics fallback active).</p>
        </div>
        <div className="flex bg-slate-950/80 p-1.5 rounded-2xl border border-slate-800 text-xs font-semibold tracking-wide shadow-inner overflow-hidden select-none">
          {useHeuristicsOnly ? (
            <span className="px-4 py-1.5 flex items-center gap-2 text-slate-400">
              <span className="w-2.5 h-2.5 rounded-full bg-slate-500"></span> Offline (Local Heuristics)
            </span>
          ) : isRateLimited ? (
            <span className="px-4 py-1.5 flex items-center gap-2 text-amber-500 bg-amber-950/20 rounded-xl">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></span> Quota Exceeded (Local Fallback)
            </span>
          ) : (
            <>
              <span className="px-4 py-1.5 flex items-center gap-2 text-amber-500 bg-amber-950/20 rounded-xl border border-amber-900/30">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></span> AI Engine Unwired
              </span>
              <span className="px-4 py-1.5 border-l border-slate-800 text-slate-500">Token-Saver Mode</span>
            </>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-8">
        {/* Left Column: Input Text */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-3">
              <h3 className="text-lg font-bold text-white">Source Material</h3>
              {inputText && (
                <span className="text-xs font-bold text-indigo-400 bg-indigo-950/50 px-3 py-1 rounded-full border border-indigo-900/30">
                  {inputStats.words} words • {inputStats.time}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer select-none">
                <input 
                  type="checkbox" 
                  checked={useHeuristicsOnly} 
                  onChange={e => setUseHeuristicsOnly(e.target.checked)} 
                  disabled={isProcessing || isRateLimited}
                  className="rounded border-slate-700 bg-slate-950 text-indigo-600 focus:ring-indigo-500/30 focus:ring-offset-slate-900"
                />
                Fast Offline Mode
              </label>
              <div className="relative">
                <input 
                  type="file" 
                  id="file-upload" 
                  accept=".txt,.md,.docx"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={handleFileUpload}
                  disabled={isProcessing}
                />
                <button disabled={isProcessing} className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-300 bg-slate-950 border border-slate-800 rounded-xl hover:bg-slate-900 transition-colors disabled:opacity-50">
                  <Upload size={16} /> Import File
                </button>
              </div>
            </div>
          </div>

          {toastMessage && (
            <div className="p-3 bg-indigo-600/90 text-white text-xs font-bold rounded-xl border border-indigo-400/50 shadow-lg flex items-center justify-between animate-fadeIn">
              <span>{toastMessage}</span>
              <button onClick={() => setToastMessage(null)} className="text-indigo-200 hover:text-white font-bold ml-2">✕</button>
            </div>
          )}

          {/* Quick Presets Bar */}
          <div className="flex flex-wrap items-center gap-2 bg-slate-950/40 p-2.5 rounded-xl border border-slate-800">
            <span className="text-[11px] text-slate-400 font-semibold uppercase tracking-wider">Test Presets:</span>
            <button
              type="button"
              onClick={() => {
                setInputText(SAMPLE_AI_ESSAY);
                showToast("Loaded AI Cliché Essay sample!");
              }}
              disabled={isProcessing}
              className="px-2.5 py-1 rounded-lg bg-indigo-950/60 hover:bg-indigo-900 border border-indigo-500/30 text-indigo-300 text-xs font-medium transition-colors cursor-pointer"
            >
              ✨ AI Cliché Essay
            </button>
            <button
              type="button"
              onClick={() => {
                setInputText(SAMPLE_CORPORATE);
                showToast("Loaded Corporate Slop sample!");
              }}
              disabled={isProcessing}
              className="px-2.5 py-1 rounded-lg bg-purple-950/60 hover:bg-purple-900 border border-purple-500/30 text-purple-300 text-xs font-medium transition-colors cursor-pointer"
            >
              ✨ Corporate Slop
            </button>
            {inputText && (
              <button
                type="button"
                onClick={() => setInputText("")}
                disabled={isProcessing}
                className="px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-400 hover:text-white text-xs font-medium transition-colors ml-auto cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>

          <textarea 
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={isProcessing}
            placeholder="Paste your AI-generated text or click a sample preset above to test the heuristic refactor..."
            className="w-full h-96 p-5 bg-slate-950 border border-slate-800 rounded-2xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none resize-none transition-all disabled:opacity-50 text-slate-300 font-serif leading-relaxed text-lg"
          />

          <div className="flex items-center justify-between bg-slate-950/50 p-5 rounded-2xl border border-slate-800 shadow-xl">
            <div className="flex-1">
              {isProcessing ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm font-semibold text-slate-300">
                    <span className="flex items-center gap-2">
                      <Loader size={16} className="animate-spin text-indigo-400" />
                      Refactoring Sentences...
                    </span>
                    <span className="font-mono text-indigo-400">{progress.current} / {progress.total}</span>
                  </div>
                  <div className="w-full h-2.5 bg-slate-900 rounded-full overflow-hidden border border-slate-800">
                    <div 
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-300 ease-out shadow-[0_0_10px_rgba(99,102,241,0.5)]" 
                      style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500 font-light">Ready to process {inputText.trim() ? `${inputText.split(/\s+/).length} words` : "empty material"}.</p>
              )}
            </div>
            
            <div className="ml-6 flex items-center gap-3">
              {isProcessing ? (
                <button onClick={handleStop} className="flex items-center gap-2 px-6 py-3 bg-red-950/80 hover:bg-red-900 border border-red-800/50 text-red-200 font-bold rounded-xl transition-all shadow-lg shadow-red-950/20">
                  <Square size={16} /> Stop
                </button>
              ) : (
                <button onClick={startProcessing} disabled={!inputText.trim()} className="flex items-center gap-2 px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all disabled:bg-slate-800 disabled:text-slate-600 disabled:cursor-not-allowed shadow-lg shadow-indigo-950/50">
                  <Play size={16} fill="currentColor" /> Compile Document
                </button>
              )}
            </div>
          </div>

          {isProcessing && currentSentence && (
            <div className="p-5 bg-amber-950/20 border border-amber-900/30 rounded-2xl text-sm text-amber-300 animate-pulse">
              <span className="font-bold block mb-1">Scanning Sentence:</span>
              <p className="italic font-light">"{currentSentence}"</p>
            </div>
          )}
        </div>

        {/* Right Column: Interventions & Cleaned Output */}
        <div className="space-y-6 flex flex-col h-full">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-white">Interventions Log</h3>
            <div className="flex items-center gap-3">
              <button
                onClick={downloadLogs}
                disabled={logs.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-300 bg-slate-950 border border-slate-800 rounded-xl hover:bg-slate-900 transition-colors disabled:opacity-50"
              >
                <Download size={14} /> Download JSON
              </button>
              <span className="text-xs font-bold px-3 py-1.5 bg-slate-950 border border-slate-800 rounded-xl text-indigo-400 font-mono">
                {logs.length} interventions
              </span>
            </div>
          </div>

          {/* Log Window */}
          <div className="flex-1 bg-slate-950 border border-slate-800 rounded-2xl flex flex-col overflow-hidden min-h-[280px] max-h-[350px] shadow-inner">
             <div className="flex-1 overflow-y-auto p-5 space-y-4">
               {logs.length === 0 ? (
                 <div className="h-full flex flex-col items-center justify-center text-slate-600 py-12">
                   <History size={36} className="mb-2 opacity-25" />
                   <p className="text-sm font-light">No structural interventions logged yet.</p>
                 </div>
               ) : (
                 logs.map((log, index) => (
                   <div key={log.id} className="p-4 bg-slate-900/60 border border-slate-800/80 rounded-xl text-sm space-y-2">
                     <div className="flex items-start gap-2.5">
                       {log.action === 'delete' ? (
                         <div className="px-2 py-0.5 text-[0.65rem] uppercase tracking-widest font-black bg-red-950 text-red-400 rounded-md border border-red-900/50 mt-0.5">Deleted</div>
                       ) : (
                         <div className="px-2 py-0.5 text-[0.65rem] uppercase tracking-widest font-black bg-purple-950 text-purple-400 rounded-md border border-purple-900/50 mt-0.5">Rewritten</div>
                       )}
                       <p className="font-bold text-slate-200">{log.reason}</p>
                     </div>
                     <div className="pl-6 space-y-2 text-slate-400 leading-relaxed font-light">
                        <p className="line-through opacity-50">"{log.original}"</p>
                        {log.action === "replace" && (
                          <p className="text-slate-100 font-medium">"{log.modified}"</p>
                        )}
                     </div>
                   </div>
                 ))
               )}
               <div ref={logsEndRef} />
             </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between pt-4 gap-4">
            <div className="flex items-baseline gap-3">
              <h3 className="text-lg font-bold text-white">Cleaned Document</h3>
              {resultText && (
                <span className="text-xs font-bold text-indigo-400 bg-indigo-950/50 px-3 py-1 rounded-full border border-indigo-900/30">
                  {resultStats.words} words • {resultStats.time}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleCopy}
                disabled={!resultText}
                className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-slate-300 bg-slate-950 border border-slate-800 rounded-xl hover:bg-slate-900 transition-colors disabled:opacity-50"
              >
                {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />} 
                {copied ? "Copied" : "Copy Document"}
              </button>
              <div className="w-px h-6 bg-slate-800 mx-2"></div>
              <button 
                onClick={() => downloadText("txt")}
                disabled={!resultText}
                className="flex items-center gap-2 px-3 py-2 text-sm font-bold text-slate-300 bg-slate-950 border border-slate-800 rounded-xl hover:bg-slate-900 transition-colors disabled:opacity-50"
              >
                <Download size={14} /> .txt
              </button>
              <button 
                onClick={() => downloadText("md")}
                disabled={!resultText}
                className="flex items-center gap-2 px-3 py-2 text-sm font-bold text-slate-300 bg-slate-950 border border-slate-800 rounded-xl hover:bg-slate-900 transition-colors disabled:opacity-50"
              >
                <Download size={14} /> .md
              </button>
            </div>
          </div>

          <textarea 
            value={resultText}
            readOnly
            className="w-full h-64 p-5 bg-slate-950/60 border border-slate-850 rounded-2xl outline-none resize-none font-serif leading-relaxed text-slate-200 text-lg shadow-inner"
            placeholder="Cleaned, human-sounding text will compile here..."
          />

          {/* Theirloom Memoir & Family History CTA Banner */}
          <div className="bg-slate-950/60 border border-rose-500/30 rounded-2xl p-4 shadow-[0_10px_30px_rgba(244,63,94,0.15)] flex flex-col md:flex-row items-center justify-between gap-4 mt-6 animate-in fade-in duration-500">
              <div className="flex items-center gap-3 text-center md:text-left">
                  <div className="p-2 bg-rose-500/10 rounded-xl border border-rose-500/20 text-rose-400">
                      <Sparkles className="w-5 h-5 animate-pulse" />
                  </div>
                  <div>
                      <h4 className="font-bold text-sm text-slate-200">Writing a memoir or family history?</h4>
                      <p className="text-xs text-slate-400 mt-0.5">Securely export your cleaned narratives to <strong className="text-rose-400 font-semibold">Theirloom</strong>. Transform raw text and shoebox memories into beautiful, illustrated graphic novels or archival legacy books.</p>
                  </div>
              </div>
              <a 
                  href="https://theirloom.io" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-full md:w-auto px-5 py-2 bg-gradient-to-r from-rose-600 to-rose-700 hover:from-rose-500 hover:to-rose-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-rose-950/50 flex items-center justify-center gap-1.5 transition-all hover:scale-[1.03] shrink-0"
              >
                  Preserve Legacy →
              </a>
          </div>
        </div>
      </div>
    </div>
  );
};
